import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import DatePicker from "react-datepicker"
import { DateTime } from "luxon"
import useStore from "../../../store"
import useUserType from "../../../hooks/useUserType"
import saleOptions, { employeeSaleOptions } from "../../../constants/saleTypeOptions"
import { NeutralButton, HappyButton } from "../../common/Buttons"
import ReusableDrawer from "../../common/ReusableDrawer"
import SaleTypeSelect from "./SaleTypeSelect"
import FinancialCompanyInput from "./FinancialCompanyInput"
import UploadUnitFile from "./UploadUnitFile"

const BOS_NAME = "Bill Of Sale (BOS)"

export default function UploadBos({ unit }) {
  const { t } = useTranslation()
  const { isEmployee } = useUserType()
  const { createUnitFile, creatingUnitFile, fetchUnitFiles, markUnitSold, markingUnitSold, unitFiles } = useStore()

  const soldOn = DateTime.fromFormat(unit.attributes['sold-on'] ?? '', 'MM/dd/yyyy')
  const currentSoldOnDate = soldOn.isValid ? soldOn.toJSDate() : new Date()
  const currentSaleType = Number(unit.attributes['sale-type']) || 2 //Cash
  const currentFundingSource = unit.attributes['funding-source'] ?? ''

  const [isOpen, setIsOpen] = useState(false)
  const [bosFiles, setBosFiles] = useState([])
  const [soldOnDate, setSoldOnDate] = useState(currentSoldOnDate)
  const [saleType, setSaleType] = useState([currentSaleType])
  const [fundingSource, setFundingSource] = useState(currentFundingSource)

  useEffect(() => {
    if (!isOpen) return

    fetchUnitFiles(unit.id)
    setBosFiles([])
    setSoldOnDate(currentSoldOnDate)
    setSaleType([currentSaleType])
    setFundingSource(currentFundingSource)
  }, [isOpen, unit.id])

  const existingBosFiles = unitFiles.filter((file) =>
    String(file.attributes['unit-id']) === String(unit.id) &&
    file.attributes.name === BOS_NAME
  )

  const isBosAttached = bosFiles.length > 0 || existingBosFiles.length > 0

  useEffect(() => {
    if (isBosAttached) return

    setSaleType((current) => current[0] === currentSaleType ? current : [currentSaleType])
  }, [isBosAttached, currentSaleType])

  const formatDate = (date) => DateTime.fromJSDate(date).toFormat('MM/dd/yyyy')

  const isFinanceSale = saleType[0] === 3 //Finance

  const isSaleDetailsChanged =
    formatDate(soldOnDate) !== formatDate(currentSoldOnDate) ||
    saleType[0] !== currentSaleType ||
    (isFinanceSale && fundingSource !== currentFundingSource)

  const handleClose = () => setIsOpen(false)

  const handleSave = async () => {
    if (bosFiles.length === 0 && !isSaleDetailsChanged) return

    for (const file of bosFiles) {
      const formData = new FormData()
      formData.append('unit_file[ufile]', file)
      formData.append('unit_file[unit_id]', unit.id)
      formData.append('unit_file[name]', BOS_NAME)
      formData.append('unit_file[description]', "Bill Of Sale")

      await createUnitFile(formData, unit.id)
    }

    if (isSaleDetailsChanged) {
      await markUnitSold(
        unit,
        soldOnDate.toLocaleDateString('en-ZA'),
        unit.attributes['sold-to'],
        saleType[0],
        isFinanceSale ? fundingSource : ''
      )
    }

    handleClose()
  }

  return (
    <>
      <NeutralButton
        text={t("Upload a BOS")}
        loading={creatingUnitFile || markingUnitSold}
        onClick={() => setIsOpen(true)}
        dataTestId="upload-bos-button"
      />
      <ReusableDrawer
        title={t("Upload a BOS")}
        onClose={handleClose}
        isOpen={isOpen}
        placement="bottom"
      >
        <div className="flex flex-col gap-6 lg:max-w-lg">
          {existingBosFiles.length > 0 && (
            <>
              <div className="flex flex-col gap-2">
                <div className="text-sm text-grey-300">
                  {t("Existing Bills of Sale")}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="existing-bos-list">
                  {existingBosFiles.map((file) => (
                    <a
                      key={file.id}
                      href={file.attributes.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-grey-100 underline underline-offset-2"
                    >
                      {file.attributes['ufile-file-name'] || file.attributes.name}
                    </a>
                  ))}
                </div>
              </div>
              <hr className="border-grey-700" />
            </>
          )}

          <UploadUnitFile files={bosFiles} setFiles={setBosFiles} />

          {isEmployee && (
            <>
              <div className="flex items-center">
                <label className="text-sm text-grey-300 mr-2">{t("Date Sold")}</label>
                <DatePicker
                  selected={soldOnDate}
                  onChange={(date) => date && setSoldOnDate(date)}
                  maxDate={currentSoldOnDate}
                  className="px-4 w-32 ml-1 bg-grey-900 border-2 border-grey-700 focus:border-grey-300 focus:ring-0 rounded"
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center">
                  <label className="text-sm text-grey-300 mr-2">{t("Sale Type")}</label>
                  <SaleTypeSelect
                    collection={isBosAttached ? saleOptions : employeeSaleOptions}
                    value={saleType}
                    onChange={setSaleType}
                  />
                </div>

                {isFinanceSale && (
                  <div className="flex items-center">
                    <label className="text-sm text-grey-300 mr-2">{t("Financial Company")}</label>
                    <FinancialCompanyInput value={fundingSource} onChange={setFundingSource} />
                  </div>
                )}
              </div>
            </>
          )}

          <HappyButton
            dataTestId="save-bos-button"
            text={t('Save')}
            loading={creatingUnitFile || markingUnitSold}
            disabled={bosFiles.length === 0 && !isSaleDetailsChanged}
            onClick={handleSave}
          />
        </div>
      </ReusableDrawer>
    </>
  )
}
